import { ITask, TDateFormatToJSDate } from "@propely/propely-typings";
import { DataTableData } from "@src/components/deprecated/DataTable/DataTable";
import { Tag } from "data-hooks/company/tags/tags.type";
import { t } from "i18next";
import { DeviationPriority } from "../../../Deviation/DeviationPriority";
import { DeviationStatus } from "../../../Deviation/DeviationStatus";
import {
  DataObjectTagField,
  DeviationDataInvoicedToField,
  DeviationDataPriorityField,
  DeviationDataStatusField,
  DeviationDataTaskTypeIDField,
  DeviationDataVisibilityField,
} from "../../DataObjectField/CustomFields/DeviationDataFields";
import { FirebaseDataContractorField } from "../../DataObjectField/CustomFields/FirebaseDataContractorField";
import { FirebaseDataApiClientField } from "../../DataObjectField/CustomFields/FirebaseDataFields";
import { FirebaseDataFirebaseFileField } from "../../DataObjectField/CustomFields/FirebaseDataFirebaseFileField";
import { FirebaseDataPropertyField } from "../../DataObjectField/CustomFields/FirebaseDataPropertyField";
import { FirebaseDataSourceField } from "../../DataObjectField/CustomFields/FirebaseDataSourceField";
import { FirebaseDataUnitField } from "../../DataObjectField/CustomFields/FirebaseDataUnitField";
import { FirebaseDataUserField } from "../../DataObjectField/CustomFields/FirebaseDataUserField";
import { PropertyDataFloorsField } from "../../DataObjectField/CustomFields/PropertyFields";
import { DataObjectField } from "../../DataObjectField/DataObjectField";
import { FirebaseDataBooleanField } from "../../DataObjectField/Generic/FirebaseDataBooleanField";
import { FirebaseDataDateField } from "../../DataObjectField/Generic/FirebaseDataDateField";
import { FirebaseDataNumberField } from "../../DataObjectField/Generic/FirebaseDataNumberField";
import { FirebaseDataStringField } from "../../DataObjectField/Generic/FirebaseDataStringField";
import { DataObjectHelper } from "../../DataObjects/DataObjectHelper";
import { FirebaseFile } from "../../FirebaseFile";
import { LocalData } from "../../LocalData";

export class ITaskHelper extends DataObjectHelper<ITask> {
  override id = () => "itask";

  override displayName = () => t("main.task");
  override displayNamePlural = () => t("main.tasks");

  createdByApiClient = new FirebaseDataApiClientField<ITask>("createdBy.apiClient", t("main.createdByApiClient"), (data) =>
    data.createdBy?.apiClient ? LocalData.apiClients.addData(data.createdBy.apiClient.id) : undefined
  );
  createdBySource = new FirebaseDataSourceField<ITask>("createdBy.source", t("main.source"), (data) => data.createdBy?.source, "source");
  taskTypeID = new DeviationDataTaskTypeIDField<ITask>("type", t("main.taskType"), (task) => task.taskTypeID, "build");
  property = new FirebaseDataPropertyField<ITask>(
    "property",
    t("main.property"),
    (task) => LocalData.properties.getData(task.propertyID),
    "business",
    false
  );
  // TODO
  units = new FirebaseDataUnitField<ITask>(
    "unitIDs",
    t("main.tenants"),
    (task) => task.unitIDs?.map((unitID) => LocalData.properties.addData(task.propertyID).getUnitWithId(unitID)),
    "meeting_room",
    true
  );
  status = new DeviationDataStatusField<ITask>("status", t("main.status"), (task) => DeviationStatus.getStatusWithId(task.status), "done");
  userID = new FirebaseDataUserField<ITask>(
    "userID",
    t("main.reportedBy"),
    (task) => (task.userID ? LocalData.users.addData(task.userID) : undefined),
    "person_add"
  );
  title = new FirebaseDataStringField<ITask>("title", t("main.title"), (task) => task.title, "title");
  description = new FirebaseDataStringField<ITask>("description", t("main.description"), (task) => task.description, "edit");
  deviationNumber = new FirebaseDataStringField<ITask>("deviationNumber", t("main.deviationNumber"), (task) => task.deviationNumber, "tag");

  priority = new DeviationDataPriorityField<ITask>(
    "priority",
    t("main.priority"),
    (task) => DeviationPriority.getPriorityWithId(task.priority ?? undefined),
    "priority_high"
  );
  tags = new DataObjectTagField<ITask>(
    "tags",
    t("main.tags"),
    (task) => (task.tags?.map((tag) => LocalData.getTagWithId(tag))?.filter(Boolean) ?? []) as Tag[],
    "category",
    true
  );
  reportDate = new FirebaseDataDateField<ITask>(
    "reportDate",
    t("main.reportDate"),
    (task) => TDateFormatToJSDate.safeParse(task.reportDate).data,
    "schedule",
    false
  );
  lastUpdate = new FirebaseDataDateField<ITask>(
    "lastUpdate",
    t("main.lastUpdated"),
    (task) => TDateFormatToJSDate.safeParse(task.lastUpdate).data,
    "update"
  );
  floors = new PropertyDataFloorsField<ITask>("floors", t("main.floors"), (task) => task.floors ?? [], "trending_up", true);
  placeDescription = new FirebaseDataStringField<ITask>(
    "placeDescription",
    t("main.placeDescription"),
    (deviation) => deviation.placeDescription,
    "place"
  );
  startDate = new FirebaseDataDateField<ITask>(
    "startDate",
    t("main.startDate"),
    (task) => TDateFormatToJSDate.safeParse(task.startDate).data,
    "play_arrow"
  );
  endDate = new FirebaseDataDateField<ITask>("endDate", t("main.endDate"), (task) => TDateFormatToJSDate.safeParse(task.deadline).data, "last_page");
  contractorGroups = new FirebaseDataContractorField<ITask>(
    "contractorGroups",
    t("main.contractorGroups"),
    (task) => task.contractorGroups?.map((contractorGroup) => LocalData.contractorGroups.addData(contractorGroup)) ?? [],
    "build_circle",
    true
  );
  responsibles = new FirebaseDataUserField<ITask>(
    "responsibles",
    t("main.responsibles"),
    (task) => task.responsibles?.map((id) => LocalData.users.addData(id)) ?? [],
    "supervisor_account",
    true
  );
  executors = new FirebaseDataUserField<ITask>(
    "executors",
    t("main.executors"),
    (deviation) => deviation.executors?.map((id) => LocalData.users.addData(id)) ?? [],
    "people_alt",
    true
  );
  archived = new FirebaseDataBooleanField<ITask>("archived", t("main.isArchived"), (task) => task.archived, "archive");
  files = new FirebaseDataFirebaseFileField<ITask>(
    "files",
    t("main.files"),
    (task) => Object.entries(task.files ?? {}).map(([id, file]) => FirebaseFile.initialize(id, file)),
    "description",
    true
  );
  seenBy = new FirebaseDataUserField<ITask>(
    "seenBy",
    t("main.seenBy"),
    (task) => Object.keys(task.seenBy ?? {})?.map((id) => LocalData.users.addData(id)),
    "visibility",
    true
  );
  visibility = new DeviationDataVisibilityField<ITask>(
    "visibility",
    t("main.visibility"),
    (task) => ({
      allTenants: task.visibility?.allTenants ?? false,
      tenants: task.visibility?.tenants ?? false,
      landlords: task.visibility?.landlords ?? false,
    }),
    "visibility_off"
  );
  invoicedTo = new DeviationDataInvoicedToField<ITask>(
    "invoicedTo",
    t("main.invoicedTo"),
    (task) =>
      task.invoicedTo
        ? {
            company: LocalData.companies.addData(task.invoicedTo.companyID),
            invoiceReceiver: LocalData.companies.addData(task.invoicedTo.companyID).invoiceReceivers.addData(task.invoicedTo.invoiceReceiverID),
          }
        : undefined,
    "attach_money"
  );
  // TODO
  responseTime = new FirebaseDataNumberField<ITask>("responseTime", t("main.responseTime"), (data) => 0, "schedule", false, {
    metric: "hours",
    rawDataMetric: "minutes",
  });
  repairTime = new FirebaseDataNumberField<ITask>("repairTime", t("main.repairTime"), (data) => 0, "schedule", false, {
    metric: "days",
    rawDataMetric: "minutes",
  });

  allFields() {
    return [
      this.taskTypeID,
      this.property,
      this.units,
      this.status,
      this.userID,
      this.title,
      this.description,
      this.deviationNumber,
      this.priority,
      this.tags,
      this.reportDate,
      this.lastUpdate,
      this.floors,
      this.placeDescription,
      this.startDate,
      this.endDate,
      this.contractorGroups,
      this.responsibles,
      this.executors,
      this.archived,
      this.files,
      this.seenBy,
      this.visibility,
      this.invoicedTo,
      this.createdByApiClient,
      this.responseTime,
      this.repairTime,
      this.createdBySource,
    ];
  }

  override getUniqueIDForData(data: ITask): string {
    return `${data.propertyID}-${data.id}`;
  }

  override getLinkTo(key: string): string | undefined {
    if (key.split("-")[0].length > 1) {
      return `/tasks/${key.split("-")[1]}`;
    }
    return undefined;
  }

  override getDefaultFilterFields = (): DataObjectField<any, any>[] => {
    return [
      this.property,
      this.taskTypeID,
      this.status,
      this.tags,
      this.reportDate,
      this.endDate,
      this.priority,
      this.responsibles,
      this.executors,
      this.contractorGroups,
    ];
  };

  getAllDataTableDatas(documents: ITask[], fields?: string[] | undefined): DataTableData[] {
    return [];
  }
}
